import { z } from 'zod';
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';

// Jordan: +962, UAE: +971 — used for customer/owner phone fields
export const phoneSchema = z
  .string()
  .regex(
    /^(\+962|00962|0)?[79]\d{7,8}$|^(\+971|00971|0)?[0-9]\d{8}$/,
    'Invalid phone number'
  );

// Looser phone for branch/staff that can have any international format
export const internationalPhoneSchema = z
  .string()
  .regex(/^\+?[0-9]{7,15}$/, 'Invalid phone number. Please enter a valid number.');

export const emailSchema = z.string().email('Invalid email address');

export const urlSchema = z.string().url('Invalid URL').optional().or(z.literal(''));

// ── E.164 phone validation (libphonenumber-js) ──────────────────────────────
// Used by the signup OTP flow (AB#1513 / AB#1516) where the country is chosen
// in a separate field and the phone must be valid for that country.

/**
 * Returns true if `input` parses as a valid mobile/fixed-line number for the
 * given ISO-3166 country code. Wraps libphonenumber-js so callers don't have
 * to import it directly.
 */
export function isValidPhoneForCountry(input: string, country: string): boolean {
  if (!input || !country) return false;
  try {
    const parsed = parsePhoneNumberFromString(input, country as CountryCode);
    return parsed?.isValid() === true;
  } catch {
    return false;
  }
}

/**
 * Parses `input` and returns the canonical E.164 representation
 * (e.g. "+971501234567"), or `undefined` if parsing/validation fails.
 * Use this just before sending to the backend so the wire format is normalized.
 */
export function normalizeE164(input: string, country: string): string | undefined {
  if (!input || !country) return undefined;
  try {
    const parsed = parsePhoneNumberFromString(input, country as CountryCode);
    return parsed?.isValid() ? parsed.number : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Splits an E.164 string into its `countryCode` (with leading '+') and
 * `phoneNumber` (national-significant digits) parts as the BE
 * `RegisterRequest` contract expects. Defends against the common
 * footgun of regex `^\+\d+` greedily matching the entire string.
 *
 * E.g. `"+971501234567"` → `{ countryCode: "+971", phoneNumber: "501234567" }`.
 * Returns `undefined` if the input is not a valid number.
 *
 * AB#1568 — used when the FE submits OTP-verified signup to /v1/auth/register.
 */
export function splitE164ForBackend(
  e164: string
): { countryCode: string; phoneNumber: string } | undefined {
  if (!e164) return undefined;
  try {
    const parsed = parsePhoneNumberFromString(e164);
    if (!parsed?.isValid()) return undefined;
    return {
      countryCode: '+' + parsed.countryCallingCode,
      phoneNumber: parsed.nationalNumber,
    };
  } catch {
    return undefined;
  }
}
